import React, { lazy, Suspense, useEffect, useState } from 'react';
import { isMobileOnly, MobileOnlyView } from 'react-device-detect';

import HomeBottomButtonOptions from './HomeBottomButtonOptions';
import ExitButton from './ExitButton';
import Actions from './Actions';
import ToolActions from './ToolActions';
import Teleport from './Teleport';
// import ToolBox from './ToolBox';

import { useConference } from '../DashboardContent/Dolby';
import { useMytaverseEvent } from '../../providers';

import MytaverseAppBar, {
  AppBarPositionEnum,
} from '@components/MytaverseAppBar';
import VirtualAssistant from './VirtualAssistant/VirtualAssistant';
import ToggleScreenSharingView from './ToggleScreenSharingView';
import Participants from '@core/dashboard/components/HomeBottomBar/Participants';
import UserAvatarButton from '@components/UserAvatarButton';
import { checkIsAdminRole } from '@helpers/role';
import AdminTools from '@core/dashboard/components/DashboardTopBar/DashboardTopBarContent/AdminTools';
import VirtualAssistantAppBar from '@core/dashboard/components/HomeBottomBar/VirtualAssistantAppBar';
import { env } from 'env';
import PersonViewToggleButton from '@core/dashboard/components/HomeBottomBar/PersonViewToggleButton';

const EventSchedulerButton = lazy(() => import('@features/EventScheduler'));
const ChatButton = lazy(() => import('@core/chat/components/ChatButton'));
const AssistantButton = lazy(() => import('@core/assistant'));

const HomeBottomBar = () => {
  const { videoStarted, toggleVideo } = useConference();
  const {
    currentRoom,
    showExitButton,
    currentParticipant,
    isHideWebUIExceptAssistant,
    isHideAllExceptTopBar,
    skyLinkExperience,
  } = useMytaverseEvent();
  const [customOrderBarIsOpen, setCustomOrderBarIsOpen] = useState(true);

  const showInviteUserBtn = currentParticipant?.eventRole
    ? checkIsAdminRole(currentParticipant.eventRole)
    : false;

  const showDolbyConferenceControls = !!currentRoom;

  useEffect(() => {
    return () => {
      if (videoStarted) {
        toggleVideo();
      }
    };
  }, [videoStarted]);

  if (!showDolbyConferenceControls) {
    return null;
  }

  const isHideUi =
    isHideWebUIExceptAssistant.flag || isHideAllExceptTopBar.flag;

  const renderBottomButtons = () => {
    if (!isMobileOnly && !customOrderBarIsOpen) {
      // we need to have the chat icon to be the last element of the bottom bar
      // when not on mobile, so when collapsed it is the chat icon shown.
      return (
        <>
          {env.REACT_APP_FEATURE_EVENT_SCHEDULER === 'true' ? (
            <Suspense fallback={null}>
              <EventSchedulerButton />
            </Suspense>
          ) : null}
          <ToggleScreenSharingView />
          <Actions />
          <Participants />
          <ToolActions />
          {!!currentRoom?.isMultiPlayer ? (
            <Suspense fallback={null}>
              <ChatButton />
            </Suspense>
          ) : null}
          <AssistantButton />
        </>
      );
    } else {
      return (
        <>
          {env.REACT_APP_FEATURE_EVENT_SCHEDULER === 'true' ? (
            <Suspense fallback={null}>
              <EventSchedulerButton />
            </Suspense>
          ) : null}
          <ToggleScreenSharingView />
          {!!currentRoom?.isMultiPlayer ? (
            <Suspense fallback={null}>
              <ChatButton />
            </Suspense>
          ) : null}
          <Participants />
          <ToolActions />
          <Actions />
          {isMobileOnly && <Teleport />}
          <AssistantButton />
        </>
      );
    }
  };

  const renderTopButtons = () => (
    <>{showInviteUserBtn ? <AdminTools /> : null}</>
  );

  if (!isMobileOnly && isHideWebUIExceptAssistant.flag) {
    return <VirtualAssistantAppBar />;
  }

  if (!isMobileOnly && isHideAllExceptTopBar.flag) {
    return null;
  }

  return (
    <>
      {isHideUi ? null : skyLinkExperience ? (
        <MytaverseAppBar
          position={AppBarPositionEnum.BottomLeft}
          isCollapsible={false}
          sx={{
            p: 1,
          }}
        >
          <PersonViewToggleButton />
        </MytaverseAppBar>
      ) : (
        <MytaverseAppBar
          position={
            isMobileOnly
              ? AppBarPositionEnum.TopLeft
              : AppBarPositionEnum.BottomLeft
          }
          isCollapsible={!isMobileOnly}
        >
          <HomeBottomButtonOptions />
        </MytaverseAppBar>
      )}
      {skyLinkExperience ? (
        <MytaverseAppBar
          position={AppBarPositionEnum.BottomRight}
          isCollapsible={false}
          sx={{
            p: 1.5,
          }}
        >
          {isMobileOnly || isHideAllExceptTopBar.flag ? null : (
            <VirtualAssistant
              sx={{
                p: 0,
                '& img': {
                  boxShadow: '0 0 8.711px 6.222px rgba(255, 255, 255, 0.50)',
                  borderRadius: '808.889px',
                  width: 36,
                  height: 36,
                },
              }}
            />
          )}
        </MytaverseAppBar>
      ) : (
        <MytaverseAppBar
          position={
            isMobileOnly
              ? AppBarPositionEnum.TopRight
              : AppBarPositionEnum.BottomRight
          }
          visiblePart={
            !isMobileOnly || isHideAllExceptTopBar.flag ? null : (
              <VirtualAssistant />
            )
          }
          isCollapsible={
            !isMobileOnly ||
            !(isHideAllExceptTopBar.flag || isHideWebUIExceptAssistant.flag)
          }
          customOrderBarIsOpen={customOrderBarIsOpen}
          setCustomOrderBarIsOpen={setCustomOrderBarIsOpen}
        >
          {/*<ToolBox />*/}

          {isMobileOnly ? renderTopButtons() : null}

          {isHideUi ? null : renderBottomButtons()}

          {isMobileOnly || isHideAllExceptTopBar.flag ? null : (
            <VirtualAssistant />
          )}

          {isMobileOnly ? <UserAvatarButton /> : null}
        </MytaverseAppBar>
      )}
      {showExitButton.flag ? (
        <MytaverseAppBar
          position={AppBarPositionEnum.BottomCenter}
          isCollapsible={false}
        >
          <ExitButton />
        </MytaverseAppBar>
      ) : null}
    </>
  );
};

export default HomeBottomBar;
